import { useOpenPageInNewTab } from 'src/app/AppAuth/usePageNavigation'
import React from 'react'
import log from 'loglevel'
import styled from 'styled-components/macro'
import palette from 'src/designSystem/variables/palette'
import { LinkIcon } from 'src/components/Icons/FileType'

import { getReceptorTemplate } from 'src/xgenerated'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { TextButton } from '../Buttons/TextButton'

export const ViewInSourceButton = ({
  receptorModelId,
  link,
  shortName,
  style,
}: {
  receptorModelId: string
  link: string
  shortName?: string
  style?: React.CSSProperties
}): JSX.Element => {
  const openInNewTab = useOpenPageInNewTab()

  let receptorName = 'Source'

  if (shortName) {
    receptorName = shortName
  } else {
    try {
      const receptorTemplate = getReceptorTemplate(receptorModelId)
      receptorName = receptorTemplate?.name || 'Source'
    } catch (e) {
      if (receptorModelId.startsWith('trr-')) {
        log.error('Error getting receptor template', e)
      } else {
        log.info(
          'Invalid receptor model id. Possibly a user uploaded document with the same caption as a linked document.',
          e,
        )
      }
    }
  }

  return (
    <TextButton
      pageDestination={link}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        openInNewTab(link)
      }}
      style={style}
    >
      {`View in ${receptorName}`}
      <LinkIcon />
    </TextButton>
  )
}

type DetailsRow = { label: string; value: string }

// Parses the detail-column contract: a JSON array of {label, value} objects,
// both strings, which a receptor can emit in place of several sparse columns.
// Returns null for anything else so callers fall back to plain-text rendering.
const parseDetailsRows = (raw?: string): DetailsRow[] | null => {
  if (!raw) {
    return null
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      !Array.isArray(parsed) ||
      parsed.length === 0 ||
      !parsed.every(
        (row): row is DetailsRow =>
          typeof row === 'object' &&
          row !== null &&
          typeof (row as DetailsRow).label === 'string' &&
          typeof (row as DetailsRow).value === 'string',
      )
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const DetailsPreview = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${palette.blue['400']};
    outline-offset: 2px;
    border-radius: 4px;
  }
`

const DetailsTable = styled.table`
  border-collapse: collapse;
  font-size: 12px;

  th,
  td {
    text-align: left;
    padding: 2px 8px 2px 0;
    vertical-align: top;
  }

  th {
    font-weight: 600;
    white-space: nowrap;
    padding-right: 12px;
  }
`

// True when a cell value carries the detail-column contract above. Used to
// decide, per column, whether cells render as a hover table — no receptor or
// evidence caption is hard-coded, so any receptor emitting the shape gets the
// table for free.
export const isDetailsValue = (value?: string): boolean =>
  parseDetailsRows(value) !== null

// Renders an evidence grid cell whose value is a Details JSON payload as a
// short text preview with a real hover table of its label/value pairs. Falls
// back to plain text if the value isn't in the expected shape.
export const DetailsCell = ({ value }: { value?: string }): JSX.Element => {
  const rows = parseDetailsRows(value)
  if (!rows) {
    return <>{value}</>
  }
  const preview = rows.map((row) => `${row.label}: ${row.value}`).join(' | ')
  return (
    <Tooltip
      id="evidence-details-cell-tooltip"
      overlayType={TooltipOverlayType.popover}
      placement={TooltipPositions.top}
      tooltipBody={
        <DetailsTable>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <th>{row.label}</th>
                <td>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </DetailsTable>
      }
    >
      {/* Focusable so the tooltip's built-in focus trigger reveals the full
          detail table for keyboard users — the preview text is ellipsized and
          there is no other way to reach the rest of it without a mouse. */}
      <DetailsPreview tabIndex={0}>{preview}</DetailsPreview>
    </Tooltip>
  )
}
